import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, FileCode2, Store } from "lucide-react";
import { Badge } from "./ui/Badge";
import { Reveal } from "./Reveal";
import { SystemLogo } from "./connector/SystemLogo";
import { publicCatalog } from "../lib/api";
import type { PublicListing } from "../lib/api";

/** The Wise Exchange, on the public site.
 *
 * Reads the live catalog rather than a hardcoded list, so what visitors see is
 * exactly what a workspace gets on day one. Renders nothing while loading or
 * if the catalog is empty — an empty shelf is worse than no shelf.
 */
export function ExchangeShowcase() {
  const [listings, setListings] = useState<PublicListing[] | null>(null);

  useEffect(() => {
    let live = true;
    publicCatalog()
      .then((rows) => live && setListings(rows))
      .catch(() => live && setListings([]));
    return () => {
      live = false;
    };
  }, []);

  if (!listings || listings.length === 0) return null;

  return (
    <section id="exchange" className="scroll-mt-20 border-y border-slate-200 bg-slate-50/70">
      <div className="mx-auto max-w-6xl px-6 py-24">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <Badge tone="brand" className="mx-auto">
              <Store className="h-3.5 w-3.5" /> Wise Exchange
            </Badge>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Connectors, ready to install
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Every workspace starts with a catalog we build, test and maintain. Install one,
              add your credentials, and you're integrated — no code, no waiting.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {listings.slice(0, 9).map((l, i) => (
            <Reveal key={l.slug} delay={Math.min(i, 5) * 70}>
              <div className="group h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-card transition-all hover:-translate-y-1 hover:shadow-lift">
                <div className="flex items-start justify-between gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-50 ring-1 ring-slate-200 transition group-hover:bg-white">
                    <SystemLogo
                      iconSlug={l.icon_slug}
                      systemType={l.system_type}
                      brandColor={l.brand_color}
                      name={l.display_name}
                      size={26}
                    />
                  </span>
                  <BadgeCheck className="h-4 w-4 text-brand-500" aria-label="Maintained by Wise 2.0" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-slate-900">{l.display_name}</h3>
                <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-500">
                  {l.description}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-slate-100 pt-4 text-xs text-slate-400">
                  {l.capability_count > 0 && (
                    <span className="font-medium text-slate-500">
                      {l.capability_count} operation{l.capability_count === 1 ? "" : "s"}
                    </span>
                  )}
                  <span>{l.param_count} settings</span>
                  {l.has_code && (
                    <span className="inline-flex items-center gap-1">
                      <FileCode2 className="h-3 w-3" /> tested
                    </span>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120}>
          <div className="mt-12 flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-slate-500">
              Need something that isn't here? Describe it in a sentence and Wise 2.0 builds
              the connector for you — usually in under two minutes.
            </p>
            <Link
              to="/exchange"
              className="focus-ring inline-flex h-11 items-center gap-2 rounded-xl bg-brand-gradient px-6 text-sm font-semibold text-white shadow-lift transition hover:-translate-y-0.5"
            >
              Browse the exchange <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
